import { Link } from 'react-router-dom';
import ServicesSubMenu from '../ServicesSubMenu';
import ServicesPricingCTA from '../ServicesPricingCTA';
import arkieByteImage from '../../assets/images/arkiebyte/arkiebyte-front-sparkles.webp';
import '../ServicesWebsites/index.css';

const outcomes = [
  { value: 'Ask', label: 'Visitors use their own words' },
  { value: 'Answer', label: 'RAG finds grounded information' },
  { value: 'Improve', label: 'Question trends reveal content gaps' },
];

const features = [
  { title: 'Your Content, Your Control', description: 'Use an admin page to add and update the business information your chatbot relies on.' },
  { title: 'Capture Missed Questions', description: 'See what visitors asked when the chatbot could not find a reliable answer, then fill the gap.' },
  { title: 'Understand What Customers Want', description: 'Review the most frequently asked questions and use real customer interest to guide your content.' },
  { title: 'Natural-Language Answers', description: 'RAG retrieves relevant facts from your content so AI can respond conversationally instead of forcing button-by-button choices.' },
  { title: 'A Character That Fits', description: 'Give us your photo or an avatar and we can bring it to life as an assistant that matches your business.' },
  { title: 'Useful Human Handoff', description: 'When the information is not available or a person is needed, guide the visitor toward the right contact path.' },
];

const differences = [
  { other: 'A rigid form with preset choices', ours: 'A natural conversation in the visitor\'s own words' },
  { other: 'Generic answers disconnected from your site', ours: 'RAG answers grounded in your pricing, policies, contracts, products, and services' },
  { other: 'A one-size-fits-all floating widget', ours: 'A custom character, voice, colors, and quick actions that fit your brand' },
  { other: 'No clear picture of what visitors need', ours: 'Missed-question capture and frequently asked question trends' },
  { other: 'Content that stays stale after launch', ours: 'A customer admin page that keeps you in control of updates' },
];

const workflow = [
  {
    title: 'Load Your Knowledge',
    description:
      'Start with your existing FAQs, website pages, service descriptions, policies, and customer support notes.',
  },
  {
    title: 'Customers Ask',
    description:
      'Visitors ask questions through your website chatbot or by SMS when text messaging is part of the setup.',
  },
  {
    title: 'AI Finds the Answer',
    description:
      'The system searches your business knowledge base and drafts a practical answer in seconds.',
  },
  {
    title: 'Your Team Stays in Control',
    description:
      'Review frequent and missed questions in the admin area, update your content, and make future answers more useful.',
  },
];

const useCases = [
  {
    title: 'E-Commerce and Retail',
    description:
      'Answer questions about orders, returns, product details, availability, store hours, and next steps before a customer abandons the purchase.',
  },
  {
    title: 'Local Services',
    description:
      'Help customers quickly understand pricing, availability, service areas, scheduling options, and how to request a quote.',
  },
  {
    title: 'Professional Services',
    description:
      'Handle appointment basics, location details, intake instructions, insurance notes, and common account questions without slowing down your staff.',
  },
  {
    title: 'Software and Membership Sites',
    description:
      'Reduce repetitive onboarding, billing, login, and feature questions while routing unusual issues to the right person.',
  },
];

const ArchitectureDiagram = () => (
  <div className="chatbot-architecture-diagram-card" aria-label="SMS and web chatbot system architecture diagram">
    <svg
      className="chatbot-architecture-diagram"
      viewBox="0 0 1400 700"
      role="img"
      aria-labelledby="chatbot-architecture-svg-title chatbot-architecture-svg-desc"
    >
      <title id="chatbot-architecture-svg-title">System Architecture: SMS and Web Chatbot</title>
      <desc id="chatbot-architecture-svg-desc">
        A system diagram showing Twilio, AWS Lambda, AWS storage and AI services,
        and web CDN components connected by SMS, web chatbot, and internal cloud flows.
      </desc>
      <defs>
        <marker id="chatbot-arrow-gold" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <polygon points="0 0, 10 3, 0 6" fill="#b8874f" />
        </marker>
        <marker id="chatbot-arrow-blue" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
          <polygon points="0 0, 10 3, 0 6" fill="#1f2937" />
        </marker>
      </defs>

      <text x="700" y="34" fontSize="24" fontWeight="800" fill="#1f2937" textAnchor="middle">
        System Architecture: SMS and Web Chatbot
      </text>

      <rect x="30" y="100" width="200" height="550" fill="#f8fafc" stroke="#1a6b7f" strokeWidth="2" rx="8" />
      <text x="130" y="140" fontSize="18" fontWeight="800" fill="#1a6b7f" textAnchor="middle">Twilio</text>
      <rect x="50" y="170" width="160" height="80" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="130" y="200" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">SMS Service</text>
      <text x="130" y="222" fontSize="11" fill="#4b5563" textAnchor="middle">Incoming/Outgoing</text>
      <text x="130" y="240" fontSize="11" fill="#4b5563" textAnchor="middle">Messages</text>
      <rect x="50" y="280" width="160" height="80" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="130" y="310" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Webhook</text>
      <text x="130" y="332" fontSize="11" fill="#4b5563" textAnchor="middle">Trigger on</text>
      <text x="130" y="350" fontSize="11" fill="#4b5563" textAnchor="middle">Message Received</text>
      <rect x="50" y="390" width="160" height="80" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="130" y="420" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Compliance</text>
      <text x="130" y="442" fontSize="11" fill="#4b5563" textAnchor="middle">STOP/OPTOUT</text>
      <text x="130" y="460" fontSize="11" fill="#4b5563" textAnchor="middle">Handling</text>

      <rect x="350" y="100" width="200" height="550" fill="#f8fafc" stroke="#1f2937" strokeWidth="2" rx="8" />
      <text x="450" y="140" fontSize="18" fontWeight="800" fill="#1f2937" textAnchor="middle">AWS Lambda</text>
      <rect x="370" y="170" width="160" height="80" fill="#ffffff" stroke="#1f2937" strokeWidth="2" rx="6" />
      <text x="450" y="195" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Webhook Handler</text>
      <text x="450" y="217" fontSize="11" fill="#4b5563" textAnchor="middle">Receives SMS</text>
      <text x="450" y="235" fontSize="11" fill="#4b5563" textAnchor="middle">from Twilio</text>
      <rect x="370" y="280" width="160" height="80" fill="#ffffff" stroke="#1f2937" strokeWidth="2" rx="6" />
      <text x="450" y="305" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">RAG Query</text>
      <text x="450" y="327" fontSize="11" fill="#4b5563" textAnchor="middle">Process and AI</text>
      <text x="450" y="345" fontSize="11" fill="#4b5563" textAnchor="middle">Inference</text>
      <rect x="370" y="390" width="160" height="80" fill="#ffffff" stroke="#1f2937" strokeWidth="2" rx="6" />
      <text x="450" y="415" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Outbound Reply</text>
      <text x="450" y="437" fontSize="11" fill="#4b5563" textAnchor="middle">Function URL</text>
      <text x="450" y="455" fontSize="11" fill="#4b5563" textAnchor="middle">to Twilio</text>

      <rect x="670" y="100" width="200" height="550" fill="#f8fafc" stroke="#1a6b7f" strokeWidth="2" rx="8" />
      <text x="770" y="140" fontSize="18" fontWeight="800" fill="#1a6b7f" textAnchor="middle">AWS Storage and</text>
      <text x="770" y="165" fontSize="18" fontWeight="800" fill="#1a6b7f" textAnchor="middle">Data Layer</text>
      <rect x="690" y="190" width="160" height="70" fill="#ffffff" stroke="#1a6b7f" strokeWidth="2" rx="6" />
      <text x="770" y="220" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">DynamoDB</text>
      <text x="770" y="247" fontSize="10" fill="#4b5563" textAnchor="middle">User Registrations</text>
      <rect x="690" y="280" width="160" height="70" fill="#ffffff" stroke="#1a6b7f" strokeWidth="2" rx="6" />
      <text x="770" y="310" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">S3 Vectors</text>
      <text x="770" y="337" fontSize="10" fill="#4b5563" textAnchor="middle">FAQ Vector DB</text>
      <rect x="690" y="370" width="160" height="70" fill="#ffffff" stroke="#1a6b7f" strokeWidth="2" rx="6" />
      <text x="770" y="400" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Bedrock</text>
      <text x="770" y="427" fontSize="10" fill="#4b5563" textAnchor="middle">LLM Inference</text>
      <rect x="690" y="460" width="160" height="70" fill="#ffffff" stroke="#1a6b7f" strokeWidth="2" rx="6" />
      <text x="770" y="490" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Secrets Manager</text>
      <text x="770" y="517" fontSize="10" fill="#4b5563" textAnchor="middle">API Keys and Tokens</text>

      <rect x="990" y="100" width="200" height="550" fill="#f8fafc" stroke="#b8874f" strokeWidth="2" rx="8" />
      <text x="1090" y="140" fontSize="18" fontWeight="800" fill="#b8874f" textAnchor="middle">Web and CDN</text>
      <rect x="1010" y="190" width="160" height="70" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="1090" y="220" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">S3</text>
      <text x="1090" y="247" fontSize="10" fill="#4b5563" textAnchor="middle">Static Website</text>
      <rect x="1010" y="280" width="160" height="70" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="1090" y="310" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">CloudFront</text>
      <text x="1090" y="337" fontSize="10" fill="#4b5563" textAnchor="middle">Global CDN Cache</text>
      <rect x="1010" y="370" width="160" height="70" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="1090" y="400" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Admin Panel</text>
      <text x="1090" y="427" fontSize="10" fill="#4b5563" textAnchor="middle">User Management</text>
      <rect x="1010" y="460" width="160" height="70" fill="#ffffff" stroke="#b8874f" strokeWidth="2" rx="6" />
      <text x="1090" y="490" fontSize="13" fontWeight="700" fill="#1f2937" textAnchor="middle">Users</text>
      <text x="1090" y="517" fontSize="10" fill="#4b5563" textAnchor="middle">Web Chatbot Access</text>

      <path d="M 250 210 L 330 210" stroke="#b8874f" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-gold)" />
      <text x="290" y="200" fontSize="10" fill="#b8874f" fontWeight="700">SMS Webhook</text>
      <path d="M 530 200 L 650 220" stroke="#1f2937" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-blue)" />
      <text x="570" y="190" fontSize="10" fill="#1f2937" fontWeight="700">Query/Update User</text>
      <path d="M 530 315 L 650 315" stroke="#1f2937" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-blue)" />
      <text x="560" y="305" fontSize="10" fill="#1f2937" fontWeight="700">Search FAQs</text>
      <path d="M 530 400 L 650 400" stroke="#1f2937" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-blue)" />
      <text x="560" y="390" fontSize="10" fill="#1f2937" fontWeight="700">AI Response</text>
      <path d="M 530 440 L 650 490" stroke="#1f2937" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-blue)" strokeDasharray="5,5" />
      <text x="570" y="475" fontSize="10" fill="#1f2937" fontWeight="700">Retrieve Keys</text>
      <path d="M 350 460 L 250 460" stroke="#b8874f" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-gold)" />
      <text x="290" y="475" fontSize="10" fill="#b8874f" fontWeight="700">Send Reply SMS</text>
      <path d="M 1090 260 L 1090 280" stroke="#b8874f" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-gold)" />
      <text x="1100" y="275" fontSize="10" fill="#b8874f" fontWeight="700">Cache</text>
      <path d="M 1090 350 L 1090 370" stroke="#b8874f" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-gold)" />
      <text x="1100" y="365" fontSize="10" fill="#b8874f" fontWeight="700">Serve</text>
      <path d="M 530 150 Q 760 80 1010 150" stroke="#1f2937" strokeWidth="2" fill="none" markerEnd="url(#chatbot-arrow-blue)" strokeDasharray="5,5" />
      <text x="750" y="70" fontSize="10" fill="#1f2937" fontWeight="700">API for Web Chatbot</text>

      <circle cx="50" cy="680" r="4" fill="#b8874f" />
      <text x="65" y="684" fontSize="11" fill="#4b5563">Twilio Flow</text>
      <circle cx="250" cy="680" r="4" fill="#1f2937" />
      <text x="265" y="684" fontSize="11" fill="#4b5563">AWS Internal Flow</text>
      <line x1="480" y1="680" x2="510" y2="680" stroke="#1f2937" strokeWidth="2" strokeDasharray="5,5" />
      <text x="520" y="684" fontSize="11" fill="#4b5563">Async/Lookup Flow</text>
    </svg>
  </div>
);

const ChatbotShowcase = () => (
  <div className="chatbot-showcase" aria-label="Friendly chatbot answering a customer and turning questions into business insights">
    <div className="chatbot-showcase-chat">
      <div className="chatbot-showcase-profile">
        <img src={arkieByteImage} alt="ArkieByte, Hogcloud's friendly chatbot character" />
        <div><strong>Your business character</strong><span>Online and ready to help</span></div>
      </div>
      <p className="chatbot-showcase-question">Do you require a contract, and what does it include?</p>
      <p className="chatbot-showcase-answer">Here’s what the business says about contracts and service details…</p>
      <div className="chatbot-showcase-source"><span>RAG</span> Answer grounded in your content</div>
    </div>
    <div className="chatbot-showcase-insights">
      <p>Customer insights</p>
      <strong>What people want to know</strong>
      <div><span>Pricing</span><i style={{ width: '88%' }} /></div>
      <div><span>Policies</span><i style={{ width: '67%' }} /></div>
      <div><span>Services</span><i style={{ width: '52%' }} /></div>
      <small>Missed questions are saved so you can improve the next answer.</small>
    </div>
  </div>
);

const ServicesChatbots = () => {
  return (
    <div className="services-placeholder-page chatbot-service-page">
      <ServicesSubMenu />

      <section className="chatbot-hero" aria-labelledby="chatbot-service-title">
        <div className="chatbot-hero-copy">
          <p className="services-placeholder-kicker">Friendly, Useful AI Chatbots</p>
          <h1 id="chatbot-service-title">Turn your business knowledge into a conversation.</h1>
          <p>
            Give customers natural-language answers about your pricing, contracts,
            policies, products, and services—without making them hunt through your site.
            You control the content, see what people ask, and choose a character that
            feels at home in your business.
          </p>
          <div className="chatbot-hero-actions">
            <Link className="chatbot-primary-link" to="/contact">Request a Demo</Link>
            <Link className="chatbot-secondary-link" to="/services/websites">Back to Website Services</Link>
          </div>
        </div>
        <ChatbotShowcase />
      </section>

      <section className="chatbot-outcomes" aria-label="Chatbot service outcomes">
        {outcomes.map((outcome) => (
          <article key={outcome.label}>
            <strong>{outcome.value}</strong>
            <span>{outcome.label}</span>
          </article>
        ))}
      </section>

      <section className="chatbot-section chatbot-two-column" aria-labelledby="chatbot-problem-title">
        <div>
          <p className="services-placeholder-kicker">Helpful by Design</p>
          <h2 id="chatbot-problem-title">A chatbot should make finding answers easier—not more annoying.</h2>
        </div>
        <div className="chatbot-copy-stack">
          <p>
            People dislike chatbots that act like a form, trap them in preset choices,
            or give generic answers. Hogcloud chatbots use Retrieval Augmented Generation
            (RAG) to find the relevant facts in your approved business content and use AI
            to respond naturally.
          </p>
          <p>
            Your customers can ask the way they normally speak. Your team stays in control
            of the source information, sees question trends, and learns what the website
            still needs to explain.
          </p>
        </div>
      </section>

      <section className="chatbot-section" aria-labelledby="chatbot-features-title">
        <p className="services-placeholder-kicker">Included Capabilities</p>
        <h2 id="chatbot-features-title">A chatbot you can manage and improve.</h2>
        <div className="chatbot-feature-grid">
          {features.map((feature) => (
            <article key={feature.title}>
              <h3>{feature.title}</h3>
              <p>{feature.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="chatbot-section chatbot-difference" aria-labelledby="chatbot-difference-title">
        <p className="services-placeholder-kicker">Why Hogcloud Is Different</p>
        <h2 id="chatbot-difference-title">More than a form that pops up.</h2>
        <div className="chatbot-comparison-head" aria-hidden="true"><span>Typical chatbot</span><span>Hogcloud chatbot</span></div>
        <div className="chatbot-comparison">
          {differences.map((difference) => (
            <article key={difference.other}>
              <p>{difference.other}</p><strong aria-hidden="true">→</strong><p>{difference.ours}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="chatbot-section" aria-labelledby="chatbot-workflow-title">
        <p className="services-placeholder-kicker">How It Works</p>
        <h2 id="chatbot-workflow-title">From your knowledge to better answers.</h2>
        <div className="chatbot-workflow">
          {workflow.map((step, index) => (
            <article key={step.title}>
              <span>{index + 1}</span>
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="chatbot-section" aria-labelledby="chatbot-industries-title">
        <p className="services-placeholder-kicker">Use Cases</p>
        <h2 id="chatbot-industries-title">Useful across customer-heavy businesses.</h2>
        <div className="chatbot-use-case-grid">
          {useCases.map((useCase) => (
            <article key={useCase.title}>
              <h3>{useCase.title}</h3>
              <p>{useCase.description}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="chatbot-cta" aria-labelledby="chatbot-cta-title">
        <h2 id="chatbot-cta-title">Ready to make customer questions easier to handle?</h2>
        <p>
          Hogcloud can review your current FAQ workload and map the first chatbot or SMS
          support workflow that would give your team time back.
        </p>
        <Link className="chatbot-primary-link" to="/contact">Start the Conversation</Link>
      </section>

      <ServicesPricingCTA />
    </div>
  );
};

export default ServicesChatbots;
